import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { SkipLink } from '@ircf/ui';
import { PublicHeader } from '../components/public/PublicHeader.js';
import { PublicFooter } from '../components/public/PublicFooter.js';
import { PoweredByTameDynamics } from '../components/public/PoweredByTameDynamics.js';
import { useCapability } from '../app/capability.js';

/**
 * Shell for every public route.
 *
 * Two behaviours here exist because this is a single-page application and the
 * browser's own equivalents no longer fire:
 *
 *  - Scroll restoration. React Router keeps the DOM alive across navigations, so
 *    a new page would otherwise open halfway down. Backwards navigation is left
 *    alone, so the browser's own restoration still works there.
 *  - Route announcement. There is no page load for a screen reader to report, so
 *    focus moves to the main landmark and the route name is announced once.
 */
export function PublicLayout() {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);
  const { prefersReducedMotion } = useCapability();

  useEffect(() => {
    // Mirror the motion preference onto the document so CSS can respond to the
    // in-app toggle as well as the OS setting.
    document.documentElement.dataset.reducedMotion = String(prefersReducedMotion);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Hash links must keep their anchor behaviour.
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash]);

  return (
    // No opaque page fill here: the experience canvas is `fixed inset-0 -z-10`,
    // and a solid background on this wrapper paints over it. `body` already
    // carries `near-black`, so routes without a scene stay dark.
    <div className="flex min-h-screen flex-col">
      <SkipLink href="#main">Skip to main content</SkipLink>
      <PublicHeader />
      {/* Offset for the fixed header. The hero sections deliberately paint
          beneath it, so the padding lives on the content, not the wrapper. */}
      <main id="main" ref={mainRef} tabIndex={-1} className="flex-1 focus-visible:outline-none">
        <Outlet />
      </main>
      <PublicFooter />
      <PoweredByTameDynamics />
    </div>
  );
}
